import { useState } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/background.png";
import bg from "assets/images/bg1.png";
import { navbarIconButton } from "examples/Navbars/DashboardNavbar/styles";
import { Checkbox, Icon, IconButton, Menu } from "@mui/material";
import MuiPhoneNumber from "mui-phone-number";
import styled from "@emotion/styled";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import NotificationItem from "examples/Items/NotificationItem";
import { setTransparentNavbar } from "context";

import * as Yup from "yup";
import { useFormik } from "formik";
import { usePostSignUpMutation } from "services/auth";
import { setToStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";
import { useAppDispatch } from "hooks/store";
import { setCredentials } from "reducers/authSlice";
import { removeFromStorage } from "helpers";
import ShowToast from "components/MDToast";
import Loader from "helpers/constants/Loader";
import useTranslation from "hooks/Translation";
import { getFromStorage } from "helpers";
import { useLanguageContext } from "context/Language";
import { setDirection } from "context";

function Cover() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const { changeLanguage } = useLanguageContext();
  const [signUpMutation, { isLoading }] = usePostSignUpMutation();
  const currentLang = getFromStorage(STORAGE_KEYS.language);
  const [isPhone, setIsPhone] = useState(false);
  const [langMenu, setLangMenu] = useState(false);
  const handleOpenLangMenu = (event) => setLangMenu(event.currentTarget);
  const handleCloseLangMenu = () => setLangMenu(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      phone: "",
      countryCode: "+971",
    },
    validationSchema: Yup.object({
      email: !isPhone
        ? Yup.string()
            .required("Email is required")
            .matches(
              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              "Enter a valid email address"
            )
        : Yup.string().notRequired(),
      phone: !isPhone
        ? Yup.string().notRequired()
        : Yup.string()
            .required("Phone Number is required")
            .min(6, "Minimum 6 digits are required")
            .max(16, "Maximum 16 digits are required"),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);

      let body = {
        ...(isPhone
          ? { phone_no: formik.values.phone }
          : { email: formik.values.email }),
        ...(isPhone ? { country_code: formik.values.countryCode } : {}),
      };

      try {
        const response = await signUpMutation(body).unwrap();

        if (!isChecked) {
          ShowToast(t.error.agreeTo, "error");
          return;
        }

        if (response?.status === 200) {
          const data = {
            key: isPhone ? body?.phone_no : body?.email,
            page: "signUp",
            type: isPhone ? "phone" : "email",
            ...(isPhone ? { countryCode: formik.values.countryCode } : {}),
          };

          ShowToast(response.message);
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          dispatch(
            setCredentials({
              user: response?.data,
              token: response?.data?.token || "",
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          navigate("/otp-verify", { state: data });
        } else {
          ShowToast(response?.message || "", "error");
        }
      } catch (error) {
        ShowToast(error?.data?.message || "", "error");
        console.log(error);
      }
    },
  });

  const handleChangePhone = (phone, country) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    formik.setFieldValue(
      "countryCode",
      country?.dialCode.includes("+") ? "" : "+" + country?.dialCode
    );
  };

  const LanguageMenu = () => (
    <Menu
      anchorEl={langMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(langMenu)}
      onClose={handleCloseLangMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        onClick={() => {
          window.location.reload();
          setToStorage(STORAGE_KEYS.language, "en");
          changeLanguage("en");
        }}
        // icon={<Icon>email</Icon>}
        title="English"
      />
      <NotificationItem
        onClick={() => {
          window.location.reload();
          setToStorage(STORAGE_KEYS.language, "ar");
          setDirection(dispatch, "rtl");
          changeLanguage("ar");
        }}
        // icon={<Icon>email</Icon>}
        title="Arabic"
      />
      <NotificationItem
        onClick={() => {
          window.location.reload();
          setToStorage(STORAGE_KEYS.language, "ru");
          changeLanguage("ru");
        }}
        // icon={<Icon>podcasts</Icon>}
        title="Russian"
      />
    </Menu>
  );

  const CustomPhoneField = styled(MuiPhoneNumber)({
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#2B604C", // Hover border color
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2B604C", // Focus border color
      },
    },
  });

  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = "#fff";

      if (setTransparentNavbar) {
        colorValue = "#fff";
      }

      return colorValue;
    },
  });

  return (
    <BasicLayout image="">
      <Grid
        container
        className="signin-container"
        sx={{ overflow: "hidden", minHeight: "100vh" }}
      >
        {/* Image Section */}
        <Grid
          item
          xs={0}
          md={6}
          sx={{
            display: { xs: "none", md: "block" },
            position: "relative",
            background: "black",
            height: "100vh",
          }}
        >
          <MDBox
            sx={{
              position: "absolute",
              backgroundColor: "rgba(0,0,0,0.7)",
              borderRadius: 2,
              margin: 5,
              padding: 1,
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
            }}
            onClick={handleOpenLangMenu}
          >
            <MDBox sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                disableRipple
                color="inhewhiterit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
              >
                <Icon sx={iconsStyle}>language</Icon>
              </IconButton>
              <MDTypography
                color="white"
                pr={1}
                sx={{ fontSize: 14, cursor: "pointer" }}
              >
                {currentLang === "en"
                  ? "En"
                  : currentLang === "ru"
                  ? "Ru"
                  : "Ar"}
              </MDTypography>
            </MDBox>
          </MDBox>
          {LanguageMenu()}
          <img
            src={bg}
            alt="Sign In"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              marginTop: "-2px",
            }}
          />
        </Grid>

        {/* Form Section */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            px: { xs: 4, sm: 8 },
          }}
        >
          {/* <Card> */}
          <MDBox sx={{ maxWidth: 500, width: "100%" }} pt={6}>
            <MDBox
              // variant="gradient"
              // bgColor="#2B604C"
              borderRadius="lg"
              // coloredShadow="info"
              mx={2}
              // mt={-3}
              // p={2}
              mb={1}
              textAlign="center"
            >
              <MDTypography variant="h4" fontWeight="medium" mt={1}>
                {t.main.createAccount}
              </MDTypography>
            </MDBox>
            <MDBox pt={2} pb={3} px={3}>
              <form onSubmit={formik.handleSubmit}>
                {isPhone ? (
                  <MDBox mb={2}>
                    <MDTypography variant="subtitle2" className="custom_label">
                      {t.main.phone}
                    </MDTypography>
                    <PhoneInput
                      country={"ae"}
                      placeholder="0 (000) 000-000"
                      enableSearch={false}
                      id="phone"
                      name="phone"
                      inputStyle={{
                        width: "100%",
                        backgroundColor: "transparent",
                        height: "45px",
                      }}
                      containerClass={`phone-input-container ${
                        formik.touched.phone && formik.errors.phone
                          ? "phone-input-error"
                          : ""
                      }`}
                      onChange={(phone, country) =>
                        handleChangePhone(phone, country)
                      }
                      onBlur={formik.handleBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // Prevent default behavior (form submission)
                          formik.handleSubmit(); // Manually submit the form
                        }
                      }}
                    />
                    {formik.touched.phone && formik.errors.phone ? (
                      <MDTypography variant="body1" className="err_msg">
                        {formik.touched.phone && formik.errors.phone}
                      </MDTypography>
                    ) : (
                      ""
                    )}
                  </MDBox>
                ) : (
                  <MDBox mb={2}>
                    <MDTypography variant="subtitle2" className="custom_label">
                      {t.main.email}
                    </MDTypography>
                    <MDInput
                      className="text_field"
                      type="text"
                      placeholder={t.main.email}
                      fullWidth
                      name="email"
                      id="email"
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      helperText={formik.touched.email && formik.errors.email}
                      error={formik.touched.email && formik.errors.email}
                      onChange={(val) => {
                        if (
                          val.target.value === " " ||
                          val.target.value === "."
                        ) {
                        } else {
                          formik.handleChange(val);
                        }
                      }}
                    />
                  </MDBox>
                )}

                <MDBox mt={4} mb={1}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    fullWidth
                    type="submit"
                  >
                    {t.auth.signUp}
                  </MDButton>
                </MDBox>
              </form>
              <MDBox display="flex" alignItems="center" ml={-1}>
                <Checkbox
                  className="checkbox_brdr"
                  checked={isChecked}
                  onChange={handleChange}
                  defaultChecked
                />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  onClick={() => navigate("/agreement")}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    // ml: -1,
                    fontSize: "14px",
                    fontWeight: "400",
                    // letterSpacing: "0.3px",
                    // lineHeight: "1.2",
                    textDecoration: "underline",
                  }}
                >
                  {t.main.agree}
                </MDTypography>
              </MDBox>
              {/* <Grid
                container
                spacing={3}
                justifyContent="center"
                sx={{ mt: 1 }}
              >
                <Grid item xs={12}>
                  {isPhone ? (
                    <MDBox sx={{ justifyContent: "center", display: "flex" }}>
                      <MDTypography
                        onClick={() => setIsPhone(false)}
                        variant="button"
                        fontWeight="regular"
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                      >
                        Sign up with Email
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox sx={{ justifyContent: "center", display: "flex" }}>
                      <MDTypography
                        onClick={() => setIsPhone(true)}
                        variant="button"
                        fontWeight="regular"
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                      >
                        Sign up with Phone
                      </MDTypography>
                    </MDBox>
                  )}
                </Grid>
              </Grid> */}
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography fontWeight="regular" variant="button">
                  {t.auth.already}{" "}
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-in"
                    variant="button"
                    color="#2B604C"
                    fontWeight="medium"
                  >
                    {t.auth.login}
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* </Card> */}
          <MDBox
            sx={{
              position: "absolute",
              bottom: 0,
              display: "flex",
              width: "100%",
              gap: 2,
              padding: "0px 10px 10px 10px",
              justifyContent: "center",
            }}
          >
            <MDTypography variant="caption" fontWeight="medium" color="text">
              &copy; {new Date().getFullYear()}, Sura Trade
            </MDTypography>

            <MDTypography
              variant="caption"
              fontWeight="medium"
              color="text"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Privacy Policy
            </MDTypography>
            <MDTypography
              variant="caption"
              fontWeight="medium"
              color="text"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Terms and Conditions
            </MDTypography>
            <MDTypography
              variant="caption"
              fontWeight="medium"
              color="text"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              About Us
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>
      <Loader loading={isLoading} />
    </BasicLayout>
  );
}

export default Cover;
